'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import {
  Camera,
  Plus,
  Trash2,
  Loader2,
  Package,
  Eye,
  ScanLine,
  Download,
  PackageCheck,
  Undo2
} from 'lucide-react';
import { QrScanDialog } from '@/components/qr-scan-dialog';
import { toast } from 'sonner';
import { Link } from '@/i18n/routing';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { SelectAllBanner } from '@/components/select-all-banner';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { PermissionGate } from '@/features/auth';
import { packingQueries } from '../services/packing.queries';
import {
  useDeleteBoxMutation,
  useUpdateBoxStatusMutation,
  useRollbackBoxMutation
} from '../services/packing.mutations';
import { PACKING_HASH } from '../constants';
import { PackingTableActionBar } from './packing-table-action-bar';
import { PackingCard } from './packing-card';
import { PackingCompactRow } from './packing-compact-row';
import type { Box, BoxesListParams, BoxStatus } from '../types';
import { exportBoxesAsCsv } from '../utils/export';

const STATUS_STYLES: Record<BoxStatus, string> = {
  available:
    'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200',
  packing:
    'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200',
  ready_for_shipping:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200',
  in_shipping:
    'bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
  in_transit:
    'bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200',
  received:
    'bg-teal-100 text-teal-800 hover:bg-teal-100 dark:bg-teal-900 dark:text-teal-200'
};

export function PackingListing() {
  const t = useTranslations('packing');
  const router = useRouter();
  const tc = useTranslations('common');
  // Overlay open-state lives in the URL hash (#delete/<id>, #rollback/<id>,
  // #scan) so overlays are deep-linkable, survive refresh, and close on browser
  // Back. Destructure into stable locals for the React Compiler dep arrays.
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(PACKING_HASH.delete);
  const {
    open: rollbackOpen,
    param: rollbackId,
    openWith: openRollback,
    onOpenChange: closeRollback
  } = useHashParamDialog(PACKING_HASH.rollback);
  const { open: scanOpen, onOpenChange: toggleScan } = useHashDialog(
    PACKING_HASH.scan
  );
  // Transient rollback-reason form input stays local — not deep-linkable.
  const [rollbackReason, setRollbackReason] = useState('');
  const [scannerActive, setScannerActive] = useState(true);
  const [hwScan, setHwScan] = useState<{ value: string; key: number } | null>(
    null
  );
  const [view, setView] = useDataView('packing');

  const { data: hwScanData, isFetching: hwScanFetching } = useQuery({
    ...packingQueries.list({ search: hwScan?.value ?? '', per_page: 1 }),
    enabled: !!hwScan
  });

  const statusLabels: Record<BoxStatus, string> = {
    available: t('statusAvailable'),
    packing: t('statusPacking'),
    ready_for_shipping: t('statusReadyForShipping'),
    in_shipping: t('statusInShipping'),
    in_transit: t('statusInTransit'),
    received: t('statusReceived')
  };

  const columns: ColumnDef<Box>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'box_number',
      accessorKey: 'box_number',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('boxNumber')} />
      ),
      cell: ({ row, column }) => (
        <Link
          href={`/dashboard/packing/${row.original.id}`}
          className='text-primary font-medium hover:underline'
        >
          <HighlightMatch
            text={row.original.box_number}
            query={getColumnSearchTerms(column)}
          />
        </Link>
      ),
      enableColumnFilter: true,
      meta: {
        label: t('searchPlaceholder'),
        variant: 'text' as const,
        placeholder: t('searchPlaceholder'),
        permission: 'boxes.column.boxNumber'
      }
    },
    {
      id: 'box_type',
      accessorFn: (row) => row.box_type?.name ?? '-',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('boxType')} />
      ),
      cell: ({ row }) =>
        row.original.box_type ? (
          <Badge variant='outline'>{row.original.box_type.name}</Badge>
        ) : (
          '-'
        ),
      enableSorting: false,
      meta: { label: t('boxType'), permission: 'boxes.column.boxType' }
    },
    {
      id: 'registrations_count',
      accessorKey: 'registrations_count',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('assignedContainers')}
        />
      ),
      cell: ({ row }) => row.original.registrations_count ?? 0,
      meta: {
        label: t('assignedContainers'),
        permission: 'boxes.column.assignedContainers'
      }
    },
    {
      id: 'security_seal_1',
      accessorKey: 'security_seal_1',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('securitySeal1')} />
      ),
      cell: ({ row }) => row.original.security_seal_1 ?? t('awaitingCreation'),
      enableSorting: false,
      meta: {
        label: t('securitySeal1'),
        permission: 'boxes.column.securitySeal1'
      }
    },
    {
      id: 'security_seal_2',
      accessorKey: 'security_seal_2',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('securitySeal2')} />
      ),
      cell: ({ row }) => row.original.security_seal_2 ?? t('awaitingCreation'),
      enableSorting: false,
      meta: {
        label: t('securitySeal2'),
        permission: 'boxes.column.securitySeal2'
      }
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status')} />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant='secondary' className={cn(STATUS_STYLES[status])}>
            {statusLabels[status] ?? status}
          </Badge>
        );
      },
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('filterStatus'),
        variant: 'multiSelect' as const,
        icon: 'solar:tag-bold-duotone',
        options: [
          { value: 'available', label: statusLabels.available },
          { value: 'packing', label: statusLabels.packing },
          {
            value: 'ready_for_shipping',
            label: statusLabels.ready_for_shipping
          },
          { value: 'in_shipping', label: statusLabels.in_shipping },
          { value: 'in_transit', label: statusLabels.in_transit },
          { value: 'received', label: statusLabels.received }
        ],
        permission: 'boxes.column.status'
      }
    },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('actions')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className='flex items-center justify-end gap-1'>
            {item.status === 'available' && (
              <PermissionGate permissions={['boxes.edit']}>
                <Button
                  variant='ghost'
                  size='icon'
                  title={t('markReadyForShipping')}
                  disabled={
                    updateStatusMutation.isPending ||
                    (item.registrations_count ?? 0) === 0
                  }
                  onClick={() =>
                    updateStatusMutation.mutate({
                      id: item.id,
                      data: { status: 'ready_for_shipping' }
                    })
                  }
                >
                  <PackageCheck className='h-4 w-4 text-green-600' />
                </Button>
              </PermissionGate>
            )}
            {item.status === 'in_transit' && (
              <PermissionGate permissions={['boxes.rollback']}>
                <Button
                  variant='ghost'
                  size='icon'
                  title={t('rollbackTitle')}
                  disabled={rollbackMutation.isPending}
                  onClick={() => {
                    setRollbackReason('');
                    openRollback(String(item.id));
                  }}
                >
                  <Undo2 className='h-4 w-4 text-amber-600' />
                </Button>
              </PermissionGate>
            )}
            <Button
              variant='ghost'
              size='icon'
              onClick={() => router.push(`/dashboard/packing/${item.id}`)}
            >
              <Eye className='h-4 w-4' />
            </Button>
            {item.status !== 'received' && (
              <PermissionGate permissions={['boxes.delete']}>
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => openDelete(String(item.id))}
                >
                  <Trash2 className='text-destructive h-4 w-4' />
                </Button>
              </PermissionGate>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  // URL state
  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'box_number',
    parseAsString.withDefault('')
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<Box>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<Box>[])
  );
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );

  const queryParams = useMemo<BoxesListParams>(() => {
    const params: BoxesListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    if (urlStatus.length > 0) {
      params.status = urlStatus.join(',');
    } else {
      params.status = 'available';
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';

    return params;
  }, [urlPage, urlPerPage, urlSearch, urlSorting, urlStatus]);

  const { data, isPending, isPlaceholderData } = useQuery(
    packingQueries.list(queryParams)
  );
  const deleteMutation = useDeleteBoxMutation();
  const updateStatusMutation = useUpdateBoxStatusMutation();
  const rollbackMutation = useRollbackBoxMutation();

  const boxes = (data?.data ?? []) as Box[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // #delete/<id> and #rollback/<id> → resolve the hash param back to the
  // matching loaded box (no fetch).
  const deleteTarget = deleteOpen
    ? (boxes.find((b) => String(b.id) === deleteId) ?? null)
    : null;
  const rollbackTarget = rollbackOpen
    ? (boxes.find((b) => String(b.id) === rollbackId) ?? null)
    : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<Box>({
    columns: filteredColumns,
    pageKey: 'packing',
    data: boxes,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('boxNumber'), value: 'box_number' },
      { label: t('assignedContainers'), value: 'registrations_count' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  const handleDeleteBox = useCallback(
    (box: Box) => {
      openDelete(String(box.id));
    },
    [openDelete]
  );

  function handleCreate() {
    router.push('/dashboard/packing/new');
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  function handleExport() {
    exportBoxesAsCsv(boxes);
    toast.success(t('exportSuccess'));
  }

  useEffect(() => {
    if (!hwScan || hwScanFetching) return;
    const results = (hwScanData?.data ?? []) as Box[];
    if (results.length > 0) {
      router.push(`/dashboard/packing/${results[0].id}`);
    } else {
      toast.error(t('notFound'));
    }
  }, [hwScanData, hwScanFetching, hwScan, router, t]);

  useHardwareScanner({
    onScan: (value) => setHwScan({ value, key: Date.now() }),
    enabled: scannerActive && !scanOpen
  });

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div />
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            onClick={handleExport}
            disabled={boxes.length === 0}
          >
            <Download className='me-2 h-4 w-4' />
            {t('export')}
          </Button>
          {IS_QR_SCANNER_ENABLED && (
            <>
              <Button
                variant='outline'
                onClick={() => {
                  setScannerActive((prev) => {
                    if (!prev) (document.activeElement as HTMLElement)?.blur();
                    return !prev;
                  });
                }}
              >
                <span className='relative me-2 flex h-2 w-2 shrink-0'>
                  <span
                    className={cn(
                      'absolute inline-flex h-full w-full rounded-full opacity-75',
                      scannerActive
                        ? 'animate-ping bg-green-400'
                        : 'bg-gray-400'
                    )}
                  />
                  <span
                    className={cn(
                      'relative inline-flex h-2 w-2 rounded-full',
                      scannerActive ? 'bg-green-500' : 'bg-gray-400'
                    )}
                  />
                </span>
                <ScanLine className='me-2 h-4 w-4' />
                {t('scanQR')}
              </Button>
              <Button
                variant='outline'
                size='icon'
                onClick={() => toggleScan(true)}
                title={tc('scanCamera')}
              >
                <Camera className='h-4 w-4' />
              </Button>
            </>
          )}
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='packing'
          />
          <PermissionGate permissions={['boxes.create']}>
            <Button onClick={handleCreate}>
              <Plus className='me-2 h-4 w-4' />
              {t('createBox')}
            </Button>
          </PermissionGate>
        </div>
      </div>

      <DataTableToolbar
        table={table}
        viewFilterPermission='boxes.view_filter'
      />

      {/* Data */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : boxes.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Package className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noBoxes')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {boxes.map((box) => (
              <PackingCard
                key={box.id}
                box={box}
                onDelete={handleDeleteBox}
                statusStyles={STATUS_STYLES}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {boxes.map((box) => (
              <PackingCompactRow
                key={box.id}
                box={box}
                onDelete={handleDeleteBox}
                statusStyles={STATUS_STYLES}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={boxes.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <PackingTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          />
        </div>
      )}

      <QrScanDialog
        open={scanOpen}
        onOpenChange={toggleScan}
        onScanValue={(value) => {
          toggleScan(false);
          setHwScan({ value, key: Date.now() });
        }}
      />

      {/* Delete confirmation — #delete/<id> */}
      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                number: deleteTarget?.box_number ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {deleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rollback dialog (in_transit → in_shipping with reason) — #rollback/<id> */}
      <Dialog
        open={rollbackOpen && !!rollbackTarget}
        onOpenChange={(open) => {
          if (!open) {
            closeRollback(false);
            setRollbackReason('');
          }
        }}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('rollbackTitle')}</DialogTitle>
            <DialogDescription>
              {t('rollbackDescription', {
                number: rollbackTarget?.box_number ?? ''
              })}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <Label htmlFor='rollback-reason'>
              {t('rollbackReason')}
              <span className='text-destructive ms-0.5'>*</span>
            </Label>
            <Textarea
              id='rollback-reason'
              value={rollbackReason}
              onChange={(e) => setRollbackReason(e.target.value)}
              placeholder={t('rollbackReasonPlaceholder')}
              rows={3}
              maxLength={500}
            />
            <p className='text-muted-foreground text-xs'>
              {rollbackReason.trim().length}/500
            </p>
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                closeRollback(false);
                setRollbackReason('');
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              type='button'
              disabled={!rollbackReason.trim() || rollbackMutation.isPending}
              onClick={async () => {
                if (!rollbackTarget) return;
                try {
                  await rollbackMutation.mutateAsync({
                    id: rollbackTarget.id,
                    reason: rollbackReason.trim()
                  });
                  closeRollback(false);
                  setRollbackReason('');
                } catch {
                  // global MutationCache handles error toast
                }
              }}
            >
              {rollbackMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('rollbackConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
